'use client';
import { Menu } from '@ark-ui/react/menu';
import { Portal } from '@ark-ui/react/portal';
import { css, cva, cx } from '@repo/panda/css';
import type React from 'react';
import type { ReactNode } from 'react';
import { createContext, useContext, useRef } from 'react';
import { Icon, type IconBaseProps } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';

const DropdownMenuContext = createContext<{
    variant: 'dark' | 'light';
}>({
    variant: 'dark',
});

const useDropdownMenuContext = () => useContext(DropdownMenuContext);

export interface DropdownMenuProps extends Omit<Menu.RootProps, 'children' | 'onSelect'> {
    children: ReactNode;
    trigger?: ReactNode;
    contentClassName?: string;
    size?: DropdownMenuVariants['size'];
    variant?: DropdownMenuVariants['variant'];
    placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'bottom' | 'top';
    offset?: number;
    closeOnSelect?: boolean;
    matchTriggerWidth?: boolean;
    onSelect?: (details: Menu.SelectionDetails) => void;
    disabled?: boolean;
    zIndex?: number;
}

export interface DropdownMenuItemProps extends Omit<Menu.ItemProps, 'children' | 'className'> {
    children: ReactNode;
    className?: string;
    destructive?: boolean;
    selected?: boolean;
    hint?: string;
    trailingContent?: ReactNode;
    variant?: 'dark' | 'light';
    leadingIcon?: MaterialSymbolName;
    leadingIconProps?: Partial<IconBaseProps>;
    trailingIcon?: MaterialSymbolName;
    trailingIconProps?: Partial<IconBaseProps>;
    onSelect?: () => void;
    value: string;
    asChild?: boolean;
}

export interface DropdownMenuSeparatorProps extends Omit<Menu.SeparatorProps, 'className'> {
    className?: string;
    variant?: 'dark' | 'light';
}

export interface DropdownMenuGroupProps extends Omit<Menu.ItemGroupProps, 'children' | 'className'> {
    children: ReactNode;
    label?: string;
    className?: string;
    variant?: 'dark' | 'light';
}

const menuContentStyles = cva({
    base: {
        outline: 'none',
        borderRadius: '16',
        boxShadow: '[0 25px 50px token(colors.scrim.default)]',
        padding: '8',
        minWidth: '[200px]',
        fontSize: 'md',
        fontWeight: 'medium',
        overflow: 'hidden',
        gap: '0',
        display: 'flex',
        flexDirection: 'column',
        backdropFilter: '[blur(12.5px)]',
        maxHeight: '[var(--available-height, calc(100vh - 32px))]',
        overflowY: 'auto',
        '& :is([data-part="item"], [data-part="radio-item"]) + :is([data-part="item"], [data-part="radio-item"])': {
            marginTop: '4',
        },
        _focus: {
            outline: 'none',
        },
    },
    variants: {
        size: {
            sm: { width: '[180px]', minWidth: '[180px]', maxWidth: '[180px]' },
            md: { width: '[260px]', minWidth: '[260px]', maxWidth: '[260px]' },
            lg: { width: '[320px]', minWidth: '[320px]', maxWidth: '[320px]' },
        },
        variant: {
            dark: {
                background:
                    '[linear-gradient(token(colors.scrim.default), token(colors.scrim.default)), linear-gradient(token(colors.fill.stronger), token(colors.fill.stronger))]',
                color: 'text.highlight',
            },
            light: {
                background: 'blackAlpha.5',
                color: 'wemod.black.80',
            },
        },
    },
    defaultVariants: {
        size: 'md',
        variant: 'dark',
    },
});

const menuItemStyles = cva({
    base: {
        display: 'flex',
        alignItems: 'center',
        padding: '12',
        gap: '12',
        borderRadius: '12',
        cursor: 'pointer',
        transition: 'all',
        textStyle: 'headerXs',
        background: 'transparent',
        border: 'none',
        width: 'full',
        textAlign: 'left',
        outline: 'none',
        _disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
            _hover: { background: 'transparent' },
        },
    },
    variants: {
        variant: {
            dark: {
                color: 'text.highlight',
                _hover: {
                    background: 'whiteAlpha.10',
                },
                _focusVisible: {
                    background: 'whiteAlpha.10',
                    outline: 'none',
                },
                _highlighted: { background: 'fill.default' },
            },
            light: {
                color: 'wemod.black.80',
                _hover: {
                    background: 'blackAlpha.10',
                },
                _focusVisible: {
                    background: 'blackAlpha.10',
                    outline: 'none',
                },
                _highlighted: { background: 'blackAlpha.10' },
            },
        },
        destructive: {
            true: {
                color: 'status.error.fg',
                _hover: {
                    background: 'status.error.surface',
                },
                _focusVisible: {
                    background: 'status.error.surface',
                },
                _highlighted: { background: 'status.error.surface' },
            },
        },
        selected: {
            true: {
                background: 'fill.strong',
                _hover: { background: 'fill.strong' },
                _focusVisible: { background: 'fill.strong' },
                _highlighted: { background: 'fill.strong' },
            },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const menuSeparatorStyles = cva({
    base: {
        height: '[1px]',
        marginTop: '8',
        marginBottom: '8',
        width: 'full',
        border: 'none',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
    },
    variants: {
        variant: {
            dark: {
                borderColor: 'whiteAlpha.10',
            },
            light: {
                borderColor: 'blackAlpha.10',
            },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const menuGroupStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    '& + &': {
        paddingTop: '2',
        marginTop: '2',
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: 'whiteAlpha.10',
    },
});

const menuGroupLabelStyles = cva({
    base: {
        padding: '[8px 12px]',
        textStyle: 'caption',
        textTransform: 'uppercase',
        letterSpacing: '[0.75px]',
    },
    variants: {
        variant: {
            dark: {
                color: 'text.secondary',
            },
            light: {
                color: 'wemod.black.60',
            },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

export type DropdownMenuVariants = NonNullable<Parameters<typeof menuContentStyles>[0]>;

export const DropdownMenu = ({
    children,
    trigger,
    contentClassName,
    size = 'md',
    variant = 'dark',
    placement = 'bottom-start',
    offset = 8,
    closeOnSelect = true,
    onOpenChange,
    disabled = false,
    matchTriggerWidth = false,
    onSelect,
    zIndex = 9999,
    ...arkUIProps
}: DropdownMenuProps) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const contentStyle = cx(menuContentStyles({ size, variant }), contentClassName);

    return (
        <Menu.Root
            closeOnSelect={closeOnSelect}
            positioning={{ placement, gutter: offset }}
            onSelect={onSelect}
            onOpenChange={onOpenChange}
            {...arkUIProps}
        >
            <Menu.Trigger ref={triggerRef} asChild disabled={disabled}>
                {trigger}
            </Menu.Trigger>
            <Portal>
                <Menu.Positioner style={{ zIndex }}>
                    <Menu.Content
                        ref={contentRef}
                        style={{
                            minWidth: matchTriggerWidth ? `${triggerRef.current?.offsetWidth}px` : 'revert-layer',
                            maxWidth: matchTriggerWidth ? `${triggerRef.current?.offsetWidth}px` : 'revert-layer',
                        }}
                        className={contentStyle}
                        onClick={(e: React.MouseEvent) => {
                            // Prevent clicks from bubbling up and closing the menu
                            e.stopPropagation();
                        }}
                        role="menu"
                        aria-label="Dropdown menu"
                        tabIndex={-1}
                    >
                        <DropdownMenuContext.Provider value={{ variant }}>{children}</DropdownMenuContext.Provider>
                    </Menu.Content>
                </Menu.Positioner>
            </Portal>
        </Menu.Root>
    );
};

export const DropdownMenuItem = ({
    children,
    disabled = false,
    className,
    destructive = false,
    selected = false,
    hint,
    trailingContent,
    variant,
    leadingIcon,
    leadingIconProps,
    trailingIcon,
    trailingIconProps,
    onSelect,
    value,
    asChild = false,
    ...arkUIProps
}: DropdownMenuItemProps) => {
    const { variant: contextVariant } = useDropdownMenuContext();
    const finalVariant = variant || contextVariant;

    return (
        <>
            {asChild ? (
                <Menu.Item value={value} {...arkUIProps} asChild>
                    {children}
                </Menu.Item>
            ) : (
                <Menu.Item
                    disabled={disabled}
                    className={cx(menuItemStyles({ destructive, selected, variant: finalVariant }), className)}
                    onSelect={onSelect}
                    value={value}
                    role="menuitem"
                    aria-label={typeof children === 'string' ? children : undefined}
                    tabIndex={disabled ? -1 : 0}
                    {...arkUIProps}
                >
                    {leadingIcon && <Icon icon={leadingIcon} size="md" {...leadingIconProps} decorative />}
                    <span
                        className={css({
                            flex: 'auto',
                            minWidth: '0',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        })}
                    >
                        {children}
                    </span>
                    {(trailingContent || hint || trailingIcon) && (
                        <span className={css({ display: 'flex', alignItems: 'center', gap: '4', flexShrink: '0' })}>
                            {trailingContent ?? (
                                <>
                                    {hint && (
                                        <span className={css({ textStyle: 'caption', color: 'text.secondary' })}>
                                            {hint}
                                        </span>
                                    )}
                                    {trailingIcon && (
                                        <Icon icon={trailingIcon} size="md" {...trailingIconProps} decorative />
                                    )}
                                </>
                            )}
                        </span>
                    )}
                </Menu.Item>
            )}
        </>
    );
};

export const DropdownMenuSeparator = ({ className, variant, ...arkUIProps }: DropdownMenuSeparatorProps) => {
    const { variant: contextVariant } = useDropdownMenuContext();
    const finalVariant = variant || contextVariant;

    return <Menu.Separator className={cx(menuSeparatorStyles({ variant: finalVariant }), className)} {...arkUIProps} />;
};

export const DropdownMenuRadioGroup = ({
    children,
    label,
    className,
    ...props
}: Menu.RadioItemGroupProps & { label?: string }) => {
    const { variant } = useDropdownMenuContext();
    return (
        <Menu.RadioItemGroup className={cx(menuGroupStyles, className)} {...props}>
            {label && <Menu.ItemGroupLabel className={menuGroupLabelStyles({ variant })}>{label}</Menu.ItemGroupLabel>}
            {children}
        </Menu.RadioItemGroup>
    );
};

export const DropdownMenuRadioItem = ({
    children,
    className,
    leadingIcon,
    leadingIconProps,
    ...props
}: Menu.RadioItemProps & Pick<DropdownMenuItemProps, 'leadingIcon' | 'leadingIconProps'>) => {
    const { variant } = useDropdownMenuContext();
    return (
        <Menu.RadioItem
            {...props}
            className={cx(
                menuItemStyles({ variant }),
                css({
                    _checked: {
                        background: 'fill.strong',
                        _hover: { background: 'fill.strong' },
                        _highlighted: { background: 'fill.strong' },
                    },
                }),
                className,
            )}
        >
            {leadingIcon && <Icon icon={leadingIcon} size="md" {...leadingIconProps} decorative />}
            <Menu.ItemText className={css({ flex: 'auto', minWidth: '0' })}>{children}</Menu.ItemText>
            <Menu.ItemIndicator>
                <Icon icon="check" size="md" decorative />
            </Menu.ItemIndicator>
        </Menu.RadioItem>
    );
};

export const DropdownMenuGroup = ({ children, label, className, variant, ...arkUIProps }: DropdownMenuGroupProps) => {
    const { variant: contextVariant } = useDropdownMenuContext();
    const finalVariant = variant || contextVariant;

    return (
        <Menu.ItemGroup className={cx(menuGroupStyles, className)} {...arkUIProps}>
            {label && (
                <Menu.ItemGroupLabel className={menuGroupLabelStyles({ variant: finalVariant })}>
                    {label}
                </Menu.ItemGroupLabel>
            )}
            {children}
        </Menu.ItemGroup>
    );
};
